import { ApolloClient } from "@apollo/client";
import { EncodedPolyLine, MarkerProps } from "@cofano/cck";
import { GetHistoryByIdQuery } from "__generated__/view/maps/graphql/map.queries.generated";
import { ChildProps, ChildPropsFromArray } from "types/commons";
import { Session } from "ezStore";
import { FetchBatchRoutesFunc } from "view/ships/types/route";
import { ActualPosition } from "../components/tabs/MapShipsTab";
import LocationMarker from "../components/markers/LocationMarker";
import { GetVoyageDocument } from "__generated__/view/voyages/graphql/voyage.queries.generated";

export enum MapTabs {
	Ships = "ships",
	Locations = "locations",
	Route = "route",
	Waterlevels = "waterlevels",
	Simulator = "simulator",
	Settings = "settings"
}

export enum MapShipTab {
	Information = "Information",
	History = "history",
	Calls = "calls"
}

export enum MapRouteType {
  Ship = "Ship",
  Location = "Location"
}

export enum CallTimeSource {
	Status = "status",
	Calculated = "calculated"
}

export enum HourWindows {
	"SIX" = 6,
	"TWELVE" = 12,
	"TWENTY_FOUR" = 24,
	"FORTY_EIGHT" = 48,
	"SEVENTY_TWO" = 72
}

const DEFAULT_CENTER = { lat: 51.8087565, lng: 4.6625973 };

function parseCenter(pos?: string): { lat: number; lng: number } | null {
	const [ lat, lng ] = (pos ?? "").split(",").map(Number);
	return lat && lng && !isNaN(lat) && !isNaN(lng) ? { lat, lng } : null;
}

export function getMapCenterPosition(
	session: Session | undefined,
	defaultPosition?: { latitude?: number | null; longitude?: number | null } | null,
): { lat: number; lng: number } {
	if (defaultPosition?.latitude && defaultPosition?.longitude) {
		return { lat: defaultPosition.latitude, lng: defaultPosition.longitude };
	}

	return parseCenter(session?.settings?.settings?.mapDashboardCenterPosition) ?? DEFAULT_CENTER;
}

export type HistoryItem = ChildProps<GetHistoryByIdQuery, "getHistoryById">;
export type HistoryVoyageItem = ChildPropsFromArray<HistoryItem, "voyages">;
export type HistoryNodeItem = ChildPropsFromArray<HistoryVoyageItem, "nodes"> & { id: string };

export function getHistoryNodes(history: HistoryItem) {
	const nodes: HistoryNodeItem[] = [];

	history.voyages.forEach((voyage, vIndex) => {
		voyage.nodes.forEach((node, nIndex) => {
			return nodes.push({
				id: `${vIndex}_${nIndex}`,
				...node,
			});
		});
	});

	return nodes;
}

export type VoyageRouteRequest = {
	reference: string;
	from: number;
	to: number;
} | {
	reference: string;
	fromPosition: {
		latitude: number;
		longitude: number;
	};
	toPosition: {
		latitude: number;
		longitude: number;
	};
};

export function getVoyageRoute(
	voyage: { id: number, voyageNumber: string },
	shipPosition: ActualPosition,
	fetchBatchRoutes: FetchBatchRoutesFunc,
	client: ApolloClient<unknown>,
) {
	const markers: MarkerProps[] = [];
	const polylines: EncodedPolyLine [] = [];

	return client.query({
		query: GetVoyageDocument,
		variables: {
			id: voyage.id,
		},
	}).then(({ data }) => {

		const requests: VoyageRouteRequest[] = [];
		const calls = data?.voyage?.calls ?? [];

		if (calls.length > 0) {
			calls.forEach((call, index: number) => {
				if (call.location.position?.latitude && call.location.position.longitude) {
					markers.push({
						lat: call.location.position.latitude,
						lng: call.location.position.longitude,
						component: () => <LocationMarker key={call.id} location={call.location} />,
					});
				}

				const nextCall = calls[index + 1];

				if (call.location.encodedPolyline) {
					polylines.push({
						color: "red",
						line: call.location.encodedPolyline,
						fillColor: "red",
						fillOpacity: 0.6,
					});
				}

				if (call.location && nextCall?.location) {
					//route in past
					if (nextCall.ata) {
						requests.push({
							reference: `call${call.id}ToCall${nextCall.id}_past`,
							from: call.location.id,
							to: nextCall.location.id,
						});
					}

					//route in present
					if (call.atd && !nextCall.ata) {
						if (call.location.position?.latitude && call.location.position.longitude && shipPosition.latitude && shipPosition.longitude) {
							requests.push({
								reference: "callToShip_past",
								fromPosition: {
									latitude: call.location.position.latitude,
									longitude: call.location.position.longitude,
								},
								toPosition: {
									latitude: shipPosition.latitude,
									longitude: shipPosition.longitude,
								},
							});
						}

						if (nextCall.location.position?.latitude && nextCall.location.position.longitude && shipPosition.latitude && shipPosition.longitude) {
							requests.push({
								reference: "shipToCall_future",
								fromPosition: {
									latitude: shipPosition.latitude,
									longitude: shipPosition.longitude,
								},
								toPosition: {
									latitude: nextCall.location.position.latitude,
									longitude: nextCall.location.position.longitude,
								},
							});
						}
					}

					//route in future
					if (!call.ata || (call.ata && !call.atd)) {
						requests.push({
							reference: `call${call.id}ToCall${nextCall.id}_future`,
							from: call.location.id,
							to: nextCall.location.id,
						});
					}
				}
			});
		} else {
			return {
				markers: markers,
				polylines: polylines,
			};
		}

		if (requests.length > 0) {
			return fetchBatchRoutes({
				variables: {
					input: {
						routes: requests,
					},
				},

			}).then(({ data }) => {
				const routes = data?.getBatchRoutes || [];
				Object.values(routes).map((r) => {
					const isPast = r.reference.split("_")[1] === "past";
					const route = r.route;

					if (route?.polyline) {
						polylines.push({
							color: isPast ? "#909090" : "#2C82CD",
							line: route.polyline,
							dashed: !isPast,
						});
					}
				});

				return {
					markers: markers,
					polylines: polylines,
				};
			});
		} else {
			return {
				markers: markers,
				polylines: polylines,
			};
		}
	});
}
